import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ShoppingCart } from 'lucide-react';
import { cartTranslations } from '@/i18n';
import { CartItemCard } from './cart/CartItemCard';
import { CartFooter } from './cart/CartFooter';
import { CartModals } from './cart/CartModals';
import type { CartItem, CartItemInput, SelectedOption } from '@/types';
import { useApp } from '@/contexts/AppContext';
import { formatCurrency } from '@/utils/currency';
import { resolveCustomerDisplayName } from '@/utils/displayName';
import {
  CATEGORY_TO_GROUP_ID,
  OPTION_NAME_TO_ID,
  GROUP_INFERENCE_KEYWORDS,
} from '@/config/optionMappings';
import { normalizeSelectedOptions } from '@/utils/selectedOptions';

export type { CartItem };

interface CartModalProps {
  items: CartItem[];
  isOpen: boolean;
  onClose: () => void;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onClearCart: () => void;
  onCheckout: () => void;
  onUpdateCartItem?: (itemId: string, updatedItem: Partial<CartItem>) => void;
  mode?: 'pickup' | 'table';
  onTableOrderComplete?: () => void;
}

export function CartModal({
  items,
  isOpen,
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onCheckout,
  onUpdateCartItem,
  mode = 'pickup',
  onTableOrderComplete
}: CartModalProps) {
  const { locale, currency } = useApp();
  const modalRef = useRef<HTMLDivElement>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<CartItem | null>(null);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const t = cartTranslations[locale];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  const handleQuantityChange = useCallback((itemId: string, change: number) => {
    const item = items.find(i => i.id === itemId);
    if (item) {
      const newQuantity = Math.max(0, item.quantity + change);
      if (newQuantity === 0) {
        onRemoveItem(itemId);
      } else {
        onUpdateQuantity(itemId, newQuantity);
      }
    }
  }, [items, onRemoveItem, onUpdateQuantity]);

  // Parse customizations back to option selections for editing
  const parseCustomizationsToOptions = useCallback((customizations: string[]) => {
    const selectedOptions: Record<string, string[]> = {};

    if (!customizations || customizations.length === 0) {
      return { selectedOptions };
    }

    customizations.forEach(customization => {
      if (!customization) return;

      // Parse "카테고리: 옵션명 (+가격)" or "옵션명 (+가격)" format
      const categoryMatch = customization.match(/^(.+?):\s*(.+?)(?:\s*\(([+-]?\d+)\))?$/);
      const simpleMatch = customization.match(/^(.+?)(?:\s*\(([+-]?\d+)\))?$/);

      let optionCategory = '';
      let optionName = '';

      if (categoryMatch) {
        optionCategory = categoryMatch[1]?.trim() || '';
        optionName = categoryMatch[2]?.trim() || '';
      } else if (simpleMatch) {
        optionName = simpleMatch[1]?.trim() || '';
      }

      // Determine group ID
      let groupId = 'extras'; // default group
      if (optionCategory) {
        groupId = CATEGORY_TO_GROUP_ID[optionCategory] || CATEGORY_TO_GROUP_ID[optionCategory.toLowerCase()] || 'extras';
      } else {
        // Try to determine group from option name using inference keywords
        const lowerOptionName = optionName.toLowerCase();
        if (GROUP_INFERENCE_KEYWORDS.mainChoice.some(m => lowerOptionName.includes(m.toLowerCase()))) {
          groupId = 'mainChoice';
        } else if (GROUP_INFERENCE_KEYWORDS.size.some(s => lowerOptionName.includes(s.toLowerCase()))) {
          groupId = 'size';
        } else if (GROUP_INFERENCE_KEYWORDS.spiceLevel.some(sp => lowerOptionName.includes(sp.toLowerCase()))) {
          groupId = 'spiceLevel';
        }
      }

      const optionId = OPTION_NAME_TO_ID[optionName] || OPTION_NAME_TO_ID[optionName.toLowerCase()] || optionName.toLowerCase().replace(/\s+/g, '_');

      // Add to selected options
      if (!selectedOptions[groupId]) {
        selectedOptions[groupId] = [];
      }
      selectedOptions[groupId].push(optionId);
    });

    return { selectedOptions };
  }, []);

  // Memoize parsed customizations to avoid infinite re-renders
  // #65: 저장된 메타데이터 우선 사용 (하드코딩 파싱 대신)
  const parsedCustomizations = useMemo(() => {
    if (!editingItem) {
      return { selectedOptions: {} };
    }

    // 저장된 메타데이터가 있으면 그것을 사용 (파싱 불필요)
    if (editingItem.selectedOptions) {
      return {
        selectedOptions: normalizeSelectedOptions(editingItem.selectedOptions),
      };
    }

    // 레거시 데이터: customizations만 있는 경우 파싱 시도
    if (editingItem.customizations) {
      return parseCustomizationsToOptions(editingItem.customizations);
    }

    return { selectedOptions: {} };
  }, [editingItem, parseCustomizationsToOptions]);

  // Convert CartItem to MenuItemWithOptions format for MenuOptionModal
  // #65: 하드코딩된 옵션 제거, 저장된 _originalOptions 사용
  const createMenuItemFromCartItem = useCallback((cartItem: CartItem) => {
    return {
      id: cartItem.menuItemId || cartItem.id,
      nameKey: cartItem.name,
      descriptionKey: cartItem.description,
      price: cartItem.originalPrice || cartItem.price,
      originalPrice: cartItem.originalPrice,
      imageUrl: cartItem.imageUrl,
      category: cartItem.category,
      isAvailable: true,
      restaurantId: cartItem.restaurantId,
      // 원본 옵션이 있으면 사용, 없으면 빈 배열 (옵션 섹션 미표시)
      _options: cartItem._originalOptions || [],
      // #280 보강: 장바구니 편집 모달에서도 판매 시간 행이 보이도록 availability 복원
      availability: cartItem._availability,
    };
  }, []);

  // Handle editing cart item options
  const handleEditItem = useCallback((item: CartItem) => {
    setEditingItem(item);
    setShowOptionModal(true);
  }, []);

  // Handle updating cart item with new options
  // #65: 하드코딩된 매핑 제거, API 옵션의 _name 필드 사용
  const handleUpdateCartItemOptions = useCallback((itemId: string, updatedItem: CartItemInput | null, quantity: number, selectedOptions: SelectedOption[] = []) => {
    if (onUpdateCartItem && editingItem) {
      const customizations: string[] = [];
      const basePrice = editingItem.originalPrice || editingItem.price;
      let totalPrice = basePrice;

      // 새로 선택된 옵션을 메타데이터 형식으로 변환
      const newSelectedOptions: Record<string, string[]> = {};
      // #497 R1 codex P1 시정: input_value 도 inputValues 형식으로 재구성 (편집
      // 저장 → 재편집 silent 누락 차단). updatedItem.inputValues 우선,
      // SelectedOption.input_value fallback.
      const newInputValues: Record<string, string> = updatedItem?.inputValues
        ? { ...updatedItem.inputValues }
        : {};
      selectedOptions.forEach(option => {
        // API 옵션은 _name 필드 사용, 없으면 name 또는 id 사용
        const optionName = resolveCustomerDisplayName(option._name, option.name) ?? option.id;
        const groupName = resolveCustomerDisplayName(option._groupName, option.groupName) ?? '';
        const price = option.additionalPrice || 0;

        totalPrice += price;

        // 커스터마이제이션 텍스트 생성
        if (groupName) {
          const priceText = price !== 0 ? ` (+${price})` : '';
          customizations.push(`${groupName}: ${optionName}${priceText}`);
        } else {
          const priceText = price !== 0 ? ` (+${price})` : '';
          customizations.push(`${optionName}${priceText}`);
        }

        // 입력형 옵션은 input_value 를 inputValues 에 매핑 (selectedOptions 는 빈 배열).
        if (option.input_value !== undefined && option.groupId) {
          newInputValues[option.groupId] = option.input_value;
          return;
        }

        // 메타데이터 저장 (선택형)
        if (option.groupId) {
          if (!newSelectedOptions[option.groupId]) {
            newSelectedOptions[option.groupId] = [];
          }
          newSelectedOptions[option.groupId].push(option.id);
        }
      });

      onUpdateCartItem(itemId, {
        name: editingItem.name,
        description: editingItem.description,
        price: totalPrice,
        originalPrice: basePrice,
        imageUrl: editingItem.imageUrl,
        category: editingItem.category,
        restaurantId: editingItem.restaurantId,
        restaurantName: editingItem.restaurantName,
        quantity,
        customizations,
        // #65: 메타데이터도 함께 업데이트
        _originalOptions: editingItem._originalOptions,
        selectedOptions: newSelectedOptions,
        // #497 R1 codex P1 시정: inputValues 도 cart item 에 보존 (편집 round-trip)
        inputValues: newInputValues,
      });
    }
    setShowOptionModal(false);
    setEditingItem(null);
  }, [editingItem, onUpdateCartItem]);

  const handleDeleteClick = useCallback((itemId: string) => {
    setItemToDelete(itemId);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (itemToDelete) {
      onRemoveItem(itemToDelete);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  }, [itemToDelete, onRemoveItem]);

  const handleDeleteConfirmClose = useCallback(() => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  }, []);

  const handleClearConfirm = useCallback(() => {
    onClearCart();
    setShowClearConfirm(false);
  }, [onClearCart]);

  const handleOrderConfirmationClose = useCallback(() => {
    setShowOrderConfirmation(false);
    onClose();
    if (onTableOrderComplete) {
      onTableOrderComplete();
    }
  }, [onClose, onTableOrderComplete]);

  const handleOptionModalClose = useCallback(() => {
    setShowOptionModal(false);
    setEditingItem(null);
  }, []);

  const handleTableOrder = useCallback(() => {
    setShowOrderConfirmation(true);
  }, []);

  // Calculate totals
  const calculateSubtotal = useCallback(() => {
    return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [items]);

  const subtotal = calculateSubtotal();
  const total = subtotal;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-primary">
      <div className="flex flex-col h-full">
        {/* Header - Full Screen Style */}
        <div className="bg-primary relative">
          <div className="flex items-center justify-center relative h-[50px] px-[20px]">
            {/* Back Button - Absolute Left */}
            <button
              onClick={onClose}
              className="absolute left-[20px] flex items-center justify-center w-[32px] h-[32px]"
            >
              <svg className="w-[12px] h-[21px]" fill="none" preserveAspectRatio="none" viewBox="0 0 12 21">
                <path d="M11.588 0.41098C11.4583 0.280705 11.3042 0.177346 11.1345 0.106823C10.9649 0.0363008 10.783 0 10.5993 0C10.4156 0 10.2337 0.0363008 10.0641 0.106823C9.89442 0.177346 9.74031 0.280705 9.61059 0.41098L0.326717 9.71087C0.223149 9.8144 0.140982 9.93738 0.0849197 10.0728C0.0288573 10.2082 0 10.3533 0 10.4999C0 10.6464 0.0288573 10.7916 0.0849197 10.9269C0.140982 11.0623 0.223149 11.1853 0.326717 11.2888L9.61059 20.5887C10.158 21.1371 11.0406 21.1371 11.588 20.5887C12.1354 20.0404 12.1354 19.1562 11.588 18.6079L3.49955 10.4943L11.5992 2.38063C12.1354 1.84345 12.1354 0.948157 11.588 0.41098Z" fill="white" />
              </svg>
            </button>

            {/* Title - Center */}
            <h1 className="font-['Noto_Sans:Bold',_'Noto_Sans_KR:Bold',_sans-serif] font-bold text-[16px] text-white tracking-[-0.32px] text-center uppercase" style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}>
              {t.cart}
            </h1>
          </div>

          {/* 테이블 세션 컨텍스트: 매장명 + 테이블 라벨(현지화) — /cart 진입 시 어느 매장의 어느 테이블인지 시각화.
              `mode` prop 은 URL `/restaurant/.../table/...` 기반이라 /cart 에서 false 이므로
              sessionStorage fallback 사용. 단 stale UI 위험 방지 (codex 1R P2 시정):
              `table_session_token` 까지 함께 검증 — token 만료/clear 시 자동 차단. */}
          {(() => {
            if (typeof window === 'undefined') return null;
            const tableNumber = window.sessionStorage.getItem('table_number');
            const tableSessionToken = window.sessionStorage.getItem('table_session_token');
            // mode === 'table' 분기 외에 sessionStorage fallback 은 token + tableNumber 둘 다 필수.
            const isTableSession = mode === 'table' || (!!tableNumber && !!tableSessionToken);
            if (!isTableSession) return null;
            const storeName = items[0]?.restaurantName || window.sessionStorage.getItem('table_store_name');
            if (!tableNumber && !storeName) return null;
            return (
              <div className="flex items-center justify-center gap-2 pb-2 px-4">
                {storeName && (
                  <span className="text-white/95 text-[13px] font-medium truncate max-w-[60%]">
                    {storeName}
                  </span>
                )}
                {tableNumber && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-0.5 text-white text-[12px] font-medium">
                    <span aria-hidden>🪑</span>
                    {/* #633 하드코딩 'Mesa' 제거 — 세션 언어로 현지화(테이블/Table/Mesa 혼재 차단).
                        dir="auto": RTL 로케일(ar)에서 라벨+숫자 bidi 순서 정합(codex P2-1). */}
                    <span dir="auto">{t.tableLabel} {tableNumber}</span>
                  </span>
                )}
              </div>
            );
          })()}
        </div>

        {/* Content Area */}
        <div className="flex-1 bg-muted overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center px-[clamp(24px,6vw,32px)] py-[clamp(40px,10vw,60px)] text-center">
              <div className="w-[clamp(60px,15vw,80px)] h-[clamp(60px,15vw,80px)] mb-[clamp(16px,4vw,24px)] opacity-50">
                <ShoppingCart className="w-full h-full text-muted-foreground" />
              </div>
              <h3 className="font-['Noto_Sans:Medium',_sans-serif] font-medium text-[clamp(18px,4.5vw,24px)] text-foreground mb-[clamp(8px,2vw,12px)] leading-normal" style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}>
                {t.emptyCart}
              </h3>
              <p className="font-['Noto_Sans:Regular',_sans-serif] font-normal text-[clamp(14px,3.5vw,16px)] text-muted-foreground leading-relaxed max-w-[280px]" style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}>
                {t.emptyCartDescription}
              </p>
            </div>
          ) : (
            <div className="px-[clamp(20px,5vw,24px)] py-[clamp(16px,4vw,20px)]">
              {/* Cart Items - Clean Design */}
              <div className="space-y-[clamp(20px,5vw,24px)]">
                {items.map((item) => (
                  <CartItemCard
                    key={item.id}
                    item={item}
                    t={t}
                    formatCurrency={(amount) => formatCurrency(amount, currency)}
                    onQuantityChange={handleQuantityChange}
                    onEditItem={handleEditItem}
                    onDeleteClick={handleDeleteClick}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer with totals and checkout button */}
        {items.length > 0 && (
          <CartFooter
            total={total}
            mode={mode}
            t={t}
            formatCurrency={(amount) => formatCurrency(amount, currency)}
            onCheckout={onCheckout}
            onTableOrder={handleTableOrder}
          />
        )}

        {/* Modals */}
        <CartModals
          showClearConfirm={showClearConfirm}
          onClearConfirmClose={() => setShowClearConfirm(false)}
          onClearConfirm={handleClearConfirm}
          showDeleteConfirm={showDeleteConfirm}
          itemToDelete={itemToDelete}
          onDeleteConfirmClose={handleDeleteConfirmClose}
          onDeleteConfirm={handleDeleteConfirm}
          showOptionModal={showOptionModal}
          editingItem={editingItem}
          parsedCustomizations={parsedCustomizations}
          onOptionModalClose={handleOptionModalClose}
          onUpdateCartItemOptions={handleUpdateCartItemOptions}
          createMenuItemFromCartItem={createMenuItemFromCartItem}
          showOrderConfirmation={showOrderConfirmation}
          onOrderConfirmationClose={handleOrderConfirmationClose}
          t={t}
        />
      </div>
    </div>
  );
}
